import { thresholdValue } from './const';
import { IPoint2d } from './IPoint2d';
import * as THREE from 'three';
import { normalizeAngle } from './utils';
import { IVector2d } from '../types/math';

export class Point2dUtils {
  static clone(point: IPoint2d) {
    const clonePt = { x: point.x, y: point.y };
    return clonePt;
  }

  static cloneArray(points: IPoint2d[]) {
    const newPoints: IPoint2d[] = [];
    for (const point of points) {
      newPoints.push(Point2dUtils.clone(point));
    }

    return newPoints;
  }

  static added(point: IPoint2d, vec: THREE.Vector2 | IPoint2d): IPoint2d {
    const addPoint = { x: point.x + vec.x, y: point.y + vec.y };
    return addPoint;
  }

  static addDistance(point: IPoint2d, distance: number, direction: IPoint2d): IPoint2d {
    const directionDistance = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
    const newPoint = {
      x: point.x + (distance * direction.x) / directionDistance,
      y: point.y + (distance * direction.y) / directionDistance
    };
    return newPoint;
  }

  static subtracted(point: IPoint2d, subPoint: IPoint2d): IPoint2d {
    const subtractPoint = { x: point.x - subPoint.x, y: point.y - subPoint.y };

    return subtractPoint;
  }

  /**
   * 计算两点之间的距离
   */
  static distanceTo(point1: IPoint2d, point2: IPoint2d): number {
    return Math.hypot(point1.x - point2.x, point1.y - point2.y);
  }

  // 求当前向量在某向量的投影标量
  static projectScalar(vector: IPoint2d, direction: IPoint2d): number {
    return (vector.x * direction.x + vector.y * direction.y) / (direction.x * direction.x + direction.y * direction.y);
  }

  /**
   * 判断两点是否相同（考虑浮点误差）
   */
  static equals(point1: IPoint2d, point2: IPoint2d, tolerance: number = thresholdValue): boolean {
    return Math.abs(point1.x - point2.x) < tolerance && Math.abs(point1.y - point2.y) < tolerance;
  }

  static toString(point: IPoint2d): string {
    return `(${point.x}, ${point.y})`;
  }

  static getCenter(point1: IPoint2d, point2: IPoint2d): IPoint2d {
    return { x: (point1.x + point2.x) / 2, y: (point1.y + point2.y) / 2 };
  }

  static getDirection(start: IPoint2d, end: IPoint2d): IVector2d {
    return Point2dUtils.getUnitDirection(start, end);
  }

  // 获取单位方向向量
  static getUnitDirection(start: IPoint2d, end: IPoint2d): IVector2d {
    const direction = getDirection(start, end);
    const length = Math.hypot(direction.x, direction.y);
    return {
      x: direction.x / length,
      y: direction.y / length
    };
  }

  // x轴正方向为0度，逆时针为正
  static getUnitDirectionByAngle(angle: number): IPoint2d {
    return { x: Math.cos(angle), y: Math.sin(angle) };
  }

  // angle为正时逆时针旋转
  static rotateDirection(direction: IPoint2d, angle: number): IPoint2d {
    return {
      x: direction.x * Math.cos(angle) - direction.y * Math.sin(angle),
      y: direction.x * Math.sin(angle) + direction.y * Math.cos(angle)
    };
  }

  /**
   * 计算与+x轴的夹角，逆时针为正
   */
  static getAngle(start: IPoint2d, end: IPoint2d): number {
    const direction = getDirection(start, end);
    return normalizeAngle(Math.atan2(direction.y, direction.x));
  }

  static rotatePoint(point: IPoint2d, center: IPoint2d, angle: number): IPoint2d {
    // 平移到原点
    const translatedX = point.x - center.x;
    const translatedY = point.y - center.y;

    // 计算旋转后的坐标
    const newX = translatedX * Math.cos(angle) - translatedY * Math.sin(angle);
    const newY = translatedX * Math.sin(angle) + translatedY * Math.cos(angle);

    // 平移回原坐标系
    return { x: newX + center.x, y: newY + center.y };
  }
}

function areDirectionsColinear(dir1: IPoint2d, dir2: IPoint2d, epsilon: number): boolean {
  // 计算叉积，判断是否共线
  const crossProduct = dir1.x * dir2.y - dir1.y * dir2.x;

  // 计算点积，判断方向是否相同（大于0）
  const dotProduct = dir1.x * dir2.x + dir1.y * dir2.y;

  return Math.abs(crossProduct) < epsilon && dotProduct > 0;
}

function getDirection(start: IPoint2d, end: IPoint2d): IPoint2d {
  return {
    x: end.x - start.x,
    y: end.y - start.y
  };
}

export function mergeColinearSegments(contour: IPoint2d[], epsilon: number = 1e-6): IPoint2d[] {
  if (contour.length < 3) return contour;

  const merged: IPoint2d[] = [contour[0]];
  let prevDir = getDirection(contour[0], contour[1]);

  const len = contour.length;
  for (let i = 1; i < len; i++) {
    const nextDir = getDirection(contour[i], contour[(i + 1) % len]);

    if (areDirectionsColinear(prevDir, nextDir, epsilon)) {
      // 方向相同，继续当前线段
      prevDir = nextDir;
    } else {
      // 方向不同，结束当前线段，开始新线段
      merged.push(contour[i]);
      prevDir = nextDir;
    }
  }

  return merged;
}

/**
 * 判断三个点是否共线
 */
function arePointsCollinear(p1: IPoint2d, p2: IPoint2d, p3: IPoint2d, epsilon: number = 1e-6): boolean {
  // 计算叉积 (p2-p1) × (p3-p2)
  const crossProduct = (p2.x - p1.x) * (p3.y - p2.y) - (p2.y - p1.y) * (p3.x - p2.x);
  // 如果叉积接近0，说明三点共线
  return Math.abs(crossProduct) <= epsilon;
}

/**
 * 去除闭环中多余的共线点
 */
export function removeCollinearPoints(closedPolygon: IPoint2d[], epsilon: number = 1e-6): IPoint2d[] {
  if (closedPolygon.length < 3) {
    return [...closedPolygon];
  }

  const result: IPoint2d[] = [];

  for (let i = 0; i < closedPolygon.length; i++) {
    const prev = closedPolygon[(i - 1 + closedPolygon.length) % closedPolygon.length];
    const current = closedPolygon[i];
    const next = closedPolygon[(i + 1) % closedPolygon.length];

    // 如果当前点是多余的共线点，则跳过
    if (arePointsCollinear(prev, current, next, epsilon)) {
      continue;
    }

    result.push(current);
  }

  // 如果所有点都共线，至少保留3个点
  if (result.length < 3) {
    // 返回原始多边形的前三个点
    return closedPolygon.slice(0, 3);
  }

  // 检查首尾两点是否也形成共线
  // 获取结果数组的首尾和第二个点
  const first = result[0];
  const last = result[result.length - 1];
  const second = result[1];

  if (arePointsCollinear(last, first, second, epsilon)) {
    // 如果首尾也共线，删除第一个点
    return result.slice(1);
  }

  return result;
}
